import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Home, 
  Search, 
  Heart, 
  Sun, 
  Moon, 
  Menu, 
  X
} from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';

// Modern SVG Logo
const ModernLogo = () => (
  <svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="2" y="6" width="32" height="24" rx="6" fill="url(#paint0_linear)"/>
    <rect x="8" y="12" width="20" height="12" rx="3" fill="#fff" fillOpacity="0.9"/>
    <rect x="12" y="16" width="4" height="4" rx="1" fill="#667eea"/>
    <rect x="20" y="16" width="4" height="4" rx="1" fill="#764ba2"/>
    <defs>
      <linearGradient id="paint0_linear" x1="2" y1="6" x2="34" y2="30" gradientUnits="userSpaceOnUse">
        <stop stopColor="#667eea"/>
        <stop offset="1" stopColor="#764ba2"/>
      </linearGradient>
    </defs>
  </svg>
);

const Navbar: React.FC = () => {
  const { isDarkMode, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Handle search submission
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
      setSearchQuery('');
      setIsMobileMenuOpen(false);
    }
  };

  // Navigation items
  const navItems = [
    { name: 'Home', path: '/', icon: Home },
    { name: 'Search', path: '/search', icon: Search },
    { name: 'Favorites', path: '/favorites', icon: Heart },
  ];

  return (
    <nav className="sticky top-0 z-50 backdrop-blur-modern bg-white/80 dark:bg-dark-900/80 border-b border-gray-200/50 dark:border-dark-700/50">
      <div className="container-modern">
        <div className="flex items-center justify-between h-16">
          {/* Modern Logo */}
          <Link to="/" className="flex items-center space-x-2 group">
            <ModernLogo />
            <span className="text-xl font-bold text-gray-900 dark:text-white group-hover:text-gradient transition-colors duration-200 tracking-tight">
              CineScope
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-6">
            {/* Search Bar */}
            <form onSubmit={handleSearch} className="relative flex items-center bg-gradient-to-br from-dark-800/80 to-dark-900/80 backdrop-blur-md border border-dark-700 shadow-xl rounded-full px-2 h-10 w-64">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 flex items-center">
                <Search className="h-4 w-4 text-primary-400" />
              </span>
              <input
                type="text"
                placeholder="Search movies & series..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 h-8 text-sm bg-transparent border-none outline-none text-white placeholder:text-primary-300 rounded-full focus:ring-2 focus:ring-primary-500 transition-all"
                style={{ minWidth: 0 }}
              />
            </form>

            {/* Navigation Links */}
            <div className="flex items-center space-x-1">
              {navItems.map((item) => (
                <Link
                  key={item.name}
                  to={item.path}
                  className="flex items-center space-x-2 px-4 py-2 rounded-lg text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-gray-100 dark:hover:bg-dark-700 transition-all duration-200"
                >
                  <item.icon className="h-4 w-4" />
                  <span className="font-medium text-sm">{item.name}</span>
                </Link>
              ))}
            </div>

            {/* Dark Mode Toggle */}
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={toggleTheme}
              className="p-2 rounded-lg bg-gray-100 dark:bg-dark-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-dark-600 transition-all duration-200"
              aria-label="Toggle dark mode"
            >
              {isDarkMode ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </motion.button>
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden">
            <motion.button
              whileTap={{ scale: 0.95 }}
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="p-2 rounded-lg bg-gray-100 dark:bg-dark-700 text-gray-700 dark:text-gray-300"
              aria-label="Toggle mobile menu"
            >
              {isMobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </motion.button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden py-4 border-t border-gray-200/50 dark:border-dark-700/50"
          >
            {/* Mobile Search */}
            <form onSubmit={handleSearch} className="mb-4 relative flex items-center bg-gradient-to-br from-dark-800/80 to-dark-900/80 backdrop-blur-md border border-dark-700 shadow-xl rounded-full px-2 h-10 w-full">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 flex items-center">
                <Search className="h-4 w-4 text-primary-400" />
              </span>
              <input
                type="text"
                placeholder="Search movies & series..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 h-8 text-sm bg-transparent border-none outline-none text-white placeholder:text-primary-300 rounded-full focus:ring-2 focus:ring-primary-500 transition-all"
                style={{ minWidth: 0 }}
              />
            </form>

            {/* Mobile Navigation Links */}
            <div className="space-y-1">
              {navItems.map((item) => (
                <Link
                  key={item.name}
                  to={item.path}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="flex items-center space-x-3 px-4 py-3 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-dark-700 rounded-lg transition-all duration-200"
                >
                  <item.icon className="h-4 w-4" />
                  <span className="font-medium text-sm">{item.name}</span>
                </Link>
              ))}
            </div>

            {/* Mobile Dark Mode Toggle */}
            <div className="mt-4 pt-4 border-t border-gray-200/50 dark:border-dark-700/50">
              <button
                onClick={toggleTheme}
                className="flex items-center space-x-3 w-full px-4 py-3 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-dark-700 rounded-lg transition-all duration-200"
              >
                {isDarkMode ? (
                  <>
                    <Sun className="h-4 w-4" />
                    <span className="font-medium text-sm">Light Mode</span>
                  </>
                ) : (
                  <>
                    <Moon className="h-4 w-4" />
                    <span className="font-medium text-sm">Dark Mode</span>
                  </>
                )}
              </button>
            </div>
          </motion.div>
        )}
      </div>
    </nav>
  );
};

export default Navbar; 